import { DataFlow } from 'app/model/dataflow/dataflow';
import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import {
    IDataFlow,
    IDataFlowMappingTypeProperties,
    IDataFlowProperties,
    IDataFlowPreviewProperties,
    IDataFlowSink,
    IDataFlowSource
} from 'app/model/adapters/dataflow/IDataflow';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import {
    DataWarehouseLinkedService,
    Folder,
    LakehouseLinkedService,
    LinkedService,
    Owned,
    OwnedSet
} from 'shell-models-lib';
import { Transformation } from 'app/model/dataflow/transformation';
import { DataFlowParameter } from 'app/model/dataflow/dataflow-parameter';
import { DataFlowFunction } from 'app/model/dataflow/dataflow-function';
import { DataFlowExpressionUtils } from './expression.util';
import { SourceAdapter } from './source.adapter';
import { SinkAdapter } from './sink.adapter';
import { IDslScriptable } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { GlobalVariables } from 'app/services/globals';
import { NamedAsyncAdapter } from 'app/model/adapters/named-async.adapter';
import { BrowseTransformation } from 'app/model/dataflow/browsetransformation';
import { ConnectTransformation } from 'app/model/dataflow/connecttransformation';
import { Group } from 'app/model/dataflow/group';
import { ParseScriptMethod } from './dataflow.parser';
import { DataFlowCleanupService } from 'app/services/dataflow-cleanup.service';
import { UserDefinedFunctionLibrary } from 'app/model/dataflow/user-defined-function-library';
import { Model } from 'app/model/backendmodel';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export interface IScriptProperties {
    parameters: Set<DataFlowParameter>;
    functions: Set<DataFlowFunction>;
    transformations: Set<Transformation>;
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DataFlow),
    asynchronous: true
})
export class DataFlowAdapter extends NamedAsyncAdapter<DataFlow, IDataFlow> {
    protected _isCombinedScriptEnabled: boolean = !GlobalVariables.splitScriptEnabled;

    public getParametersDSL(parameters: OwnedSet<DataFlowParameter>): string {
        return parameters.size
            ? `parameters{\n\t${Array.from(parameters)
                  .map((parameter) => {
                      return `${parameter.name} as ${
                          (parameter._dataflowType && parameter._dataflowType.typeInDsl) || parameter._type
                      }${
                          parameter.defaultValue
                              ? ` ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    parameter.defaultValueExpression
                                )}`
                              : ''
                      }`;
                  })
                  .join(',\n\t')}\n}`
            : '';
    }

    public getFunctionsDSL(functions: OwnedSet<DataFlowFunction>): string {
        return functions.size
            ? `functions{\n\t${Array.from(functions)
                  .map((func) => {
                      return `${func.name}(${func.paramTypes.join(',')}) as ${func.returnType} = ${
                          func.className
                              ? `'${func.className}`
                              : DataFlowExpressionUtils.wrapExpressionWithEmptyValue(func.funcExpression.expression)
                      }`;
                  })
                  .join(',\n\t')}\n}`
            : '';
    }

    public async getDSL(dataflow: DataFlow): Promise<string> {
        const sourceAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSource)) as SourceAdapter;
        const sinkAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSink)) as SinkAdapter;
        const scripts: string[] = [];

        const hasBrowseOrConnectTransformation = !!dataflow.transformations.find(
            (transformation) =>
                transformation instanceof BrowseTransformation || transformation instanceof ConnectTransformation
        );

        const dataFlowFunctionsScript = this.getFunctionsDSL(dataflow.functions);
        if (dataFlowFunctionsScript) {
            scripts.push(dataFlowFunctionsScript);
        }
        const dataFlowParametersScript = this.getParametersDSL(dataflow.parameters);
        if (dataFlowParametersScript) {
            scripts.push(dataFlowParametersScript);
        }

        // TODO: Need to fix once the browse and connect transformations are added as a transformation in a data flow
        if (!hasBrowseOrConnectTransformation) {
            for (const source of dataflow.sources) {
                scripts.push(await sourceAdapter.getDslScript(source));
            }
        }

        for (const flowlet of dataflow.sourceFlowlets) {
            scripts.push(
                await (
                    this.context.asyncAdapterFor(Metamodel.modelOf(flowlet)) as unknown as IDslScriptable
                ).getDslScript(flowlet)
            );
        }
        for (const transformation of dataflow.transformations) {
            scripts.push(
                await (
                    this.context.asyncAdapterFor(Metamodel.modelOf(transformation)) as unknown as IDslScriptable
                ).getDslScript(transformation)
            );
        }
        for (const sink of dataflow.sinks) {
            scripts.push(await sinkAdapter.getDslScript(sink));
        }

        return scripts.join('\n');
    }

    protected async _toResource(dataflow: DataFlow): Promise<IDataFlow> {
        if (!dataflow.hasParsed) {
            await dataflow.setupCache(this.context.contextData);
            const dataFlowCleanUpService = await getServiceInstance<DataFlowCleanupService>(
                this.context.contextData,
                'DataFlowCleanupService'
            );
            dataFlowCleanUpService.addDataFlowToCleanUp(dataflow);
        }
        const dataflowResource = (await super._toResource(dataflow)) as IDataFlow;
        const sourceAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSource));
        const sinkAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSink));

        dataflowResource.properties = {
            description: dataflow.description,
            folder: this.context.adapterFor(Folder.MODEL).toResource(dataflow.folder),
            type: 'MappingDataFlow',
            typeProperties: {
                sources: await Promise.all(
                    Array.from(dataflow.sources).map((source) => sourceAdapter.toResource(source))
                ),
                sinks: await Promise.all(Array.from(dataflow.sinks).map((sink) => sinkAdapter.toResource(sink)))
            }
        };

        (dataflowResource.properties.typeProperties as IDataFlowMappingTypeProperties).transformations =
            await Promise.all(
                Array.from(dataflow.transformations)
                    .concat(Array.from(dataflow.sourceFlowlets))
                    .map((transformation) =>
                        this.context.asyncAdapterFor(Metamodel.modelOf(transformation)).toResource(transformation)
                    )
            );

        if (GlobalVariables.enableGroup) {
            (dataflowResource.properties.typeProperties as IDataFlowMappingTypeProperties).groups = await Promise.all(
                Array.from(dataflow.groups).map((group) =>
                    this.context.asyncAdapterFor(Metamodel.modelOf(group)).toResource(group)
                )
            );
        }

        if (dataflow.udfLibraries.size > 0) {
            (dataflowResource.properties.typeProperties as IDataFlowMappingTypeProperties).udfLibraries = Array.from(
                dataflow.udfLibraries
            ).map(
                (lib) =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    ({ referenceName: lib.name, type: 'DataFlowReference' } as Model.DatasetReference)
            );
        }

        if (this._isCombinedScriptEnabled) {
            dataflowResource.properties.typeProperties.script = await this.getDSL(dataflow);
            if (!dataflow.savedWithSingleScript) {
                (dataflowResource.properties.typeProperties as IDataFlowMappingTypeProperties).scriptLines =
                    dataflowResource.properties.typeProperties.script.replace(/\t/g, '     ').split(/\n/g);
                delete dataflowResource.properties.typeProperties.script;
            }
        }

        // Fabric migration: map ADF linkedService name to Fabric connection ID
        if (this.context.adapterConfig.convertToFabric) {
            const lsToConnectionId = this.context.adapterConfig.linkedServiceNameToExternalConnectionId;
            if (lsToConnectionId) {
                const typeProps = dataflowResource.properties.typeProperties as IDataFlowMappingTypeProperties;

                Array.from(dataflow.sources).forEach((model, i) => {
                    // Map primary linkedService (for both inline and dataset-based sources)
                    const ls = model.sourceLinkedService ?? model.sourceDataset?.linkedService;
                    const connectionId = ls && lsToConnectionId[ls.name];
                    if (connectionId) {
                        typeProps.sources[i].linkedService = { referenceName: connectionId, type: 'LinkedServiceReference' };
                        this._applyFabricArtifactShape(typeProps.sources[i], ls);
                    }

                    // Only map schemaLinkedService if it was already serialized (e.g., CDM format)
                    if (typeProps.sources[i].schemaLinkedService) {
                        const schemaLs = model.schemaLinkedService;
                        const schemaConnectionId = schemaLs && lsToConnectionId[schemaLs.name];
                        if (schemaConnectionId) {
                            typeProps.sources[i].schemaLinkedService = { referenceName: schemaConnectionId, type: 'LinkedServiceReference' };
                        }
                    }
                });

                Array.from(dataflow.sinks).forEach((model, i) => {
                    // Map primary linkedService (for both inline and dataset-based sinks)
                    const ls = model.sinkLinkedService ?? model.sinkDataset?.linkedService;
                    const connectionId = ls && lsToConnectionId[ls.name];
                    if (connectionId) {
                        typeProps.sinks[i].linkedService = { referenceName: connectionId, type: 'LinkedServiceReference' };
                        this._applyFabricArtifactShape(typeProps.sinks[i], ls);
                    }

                    // Only map schemaLinkedService if it was already serialized (e.g., CDM format)
                    if (typeProps.sinks[i].schemaLinkedService) {
                        const schemaLs = model.schemaLinkedService;
                        const schemaConnectionId = schemaLs && lsToConnectionId[schemaLs.name];
                        if (schemaConnectionId) {
                            typeProps.sinks[i].schemaLinkedService = { referenceName: schemaConnectionId, type: 'LinkedServiceReference' };
                        }
                    }

                    // Only map rejectedDataLinkedService if it was already serialized
                    if (typeProps.sinks[i].rejectedDataLinkedService) {
                        const rejectedDataLs = model.faultToleranceLinkedService;
                        const rejectedDataConnectionId = rejectedDataLs && lsToConnectionId[rejectedDataLs.name];
                        if (rejectedDataConnectionId) {
                            typeProps.sinks[i].rejectedDataLinkedService = { referenceName: rejectedDataConnectionId, type: 'LinkedServiceReference' };
                        }
                    }
                });
            }
        }

        return dataflowResource;
    }

    protected async _toModel(resource: IDataFlow, model?: DataFlow): Promise<DataFlow> {
        if (!model) {
            model = DataFlow.MODEL.create<DataFlow>();
        }

        model = await this.parseJSONToModel(resource, model);
        await model.parseDslAsync(this.context, resource, {
            method: ParseScriptMethod.PARAMETERS
        });

        return model;
    }

    protected async parseJSONToModel(resource: IDataFlow, model: DataFlow): Promise<DataFlow> {
        await super._toModel(resource, model);
        if (!resource.properties || model instanceof UserDefinedFunctionLibrary) {
            return model;
        }

        model.folder = Owned.create<Folder>(Folder.MODEL, model);
        this.context.adapterFor(Folder.MODEL).toModel(resource.properties.folder, model.folder);

        const sourceAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSource));
        const sinkAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSink));
        const groupAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(Group));

        const props = (resource.properties as IDataFlowProperties).typeProperties as IDataFlowPreviewProperties;

        for (const sourceResource of props.sources) {
            const dataFlowSourceModel = Owned.create<DataFlowSource>(Metamodel.modelFor(DataFlowSource), model);
            await sourceAdapter.toModel(sourceResource, dataFlowSourceModel);
            model.addSource(dataFlowSourceModel);
        }

        for (const sinkResource of props.sinks) {
            const dataFlowSinkModel = Owned.create<DataFlowSink>(Metamodel.modelFor(DataFlowSink), model);
            await sinkAdapter.toModel(sinkResource, dataFlowSinkModel);
            model.addSink(dataFlowSinkModel);
        }

        if (GlobalVariables.enableGroup && props.groups) {
            for (const groupResource of props.groups) {
                const groupModel = Owned.create<Group>(Metamodel.modelFor(Group), model);
                await groupAdapter.toModel(groupResource, groupModel, model);
                model.addGroup(groupModel);
            }
        }

        return model;
    }

    private _applyFabricArtifactShape(
        resource: IDataFlowSource | IDataFlowSink,
        linkedService: LinkedService
    ): void {
        if (!(linkedService instanceof LakehouseLinkedService) &&
            !(linkedService instanceof DataWarehouseLinkedService)) {
            return;
        }

        const cachedTypeProperties = linkedService.cachedResource?.properties?.typeProperties;
        const artifactId = linkedService.artifactId ??
            linkedService.objectId ??
            cachedTypeProperties?.artifactId;
        const workspaceId = linkedService.workspaceId ??
            linkedService.workspaceObjectId ??
            cachedTypeProperties?.workspaceId;
        if (!artifactId || !workspaceId) {
            return;
        }

        const isWarehouse = linkedService instanceof DataWarehouseLinkedService;
        const endpoint = isWarehouse
            ? linkedService.endpoint ?? cachedTypeProperties?.endpoint ?? cachedTypeProperties?.endPoint
            : undefined;
        resource.type = isWarehouse ? 'DataWarehouse' : 'Lakehouse';
        resource.typeProperties = {
            ...(isWarehouse && endpoint ? { endpoint } : {}),
            artifactId,
            workspaceId
        };

        // Lakehouse/Warehouse schema is resolved from the artifact, not a second connection.
        resource.schemaLinkedService = undefined;
    }
}
